import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and user ID to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add X-User-ID header - this was missing before
  const userId = localStorage.getItem('userId');
  if (userId) {
    config.headers['X-User-ID'] = userId;
  }
  
  return config;
});

// Transform API response to match frontend expected format
const transformFolderData = (folders) => {
  if (!Array.isArray(folders)) return [];
  
  return folders.map(folder => ({
    id: folder.folderId,
    name: folder.name,
    color: folder.color,
    parentId: folder.parentFolderId,
    snippetCount: folder.snippetCount || 0,
    createdAt: folder.createdAt,
    updatedAt: folder.updatedAt,
    isRoot: folder.folderId === folder.createdBy, // Root folder ID matches user ID
    path: folder.path || '/',
    description: folder.description || '',
    tags: folder.tags || [],
    children: transformFolderData(folder.children || [])
  }));
};

// Check if folder is root folder (folder ID matches user ID)
const isRootFolder = (folder, userId) => {
  return folder.id === userId || folder.folderId === userId;
};

export const folderService = {
  // Get user's root folder
  getRootFolder: async () => {
    try {
      const response = await api.get('/api/folders/root');
      const transformedData = transformFolderData([response.data]);
      return { data: transformedData[0] };
    } catch (error) {
      console.error('Error fetching root folder:', error);
      throw error;
    }
  },

  // Get folder tree structure (backend auto-creates root folder if needed)
  getFolderTree: async () => {
    try {
      const response = await api.get('/api/folders/tree');
      const transformedData = transformFolderData(response.data);
      
      // Backend should automatically create root folder, so we trust the response
      if (transformedData.length > 1) {
        console.warn('Multiple root folders found, using first one');
      }
      
      return { data: transformedData };
    } catch (error) {
      console.error('Error fetching folder tree:', error);
      throw error;
    }
  },

  // Create new folder (defaults to root folder if no parent specified)
  createFolder: async (folderData) => {
    try {
      const payload = {
        name: folderData.name,
        parentFolderId: folderData.parentId || null, // Backend defaults to root if null
        description: folderData.description || '',
        color: folderData.color || '#3498db',
        tags: folderData.tags || []
      };

      const response = await api.post('/api/folders', payload);

      // Transform single folder response
      if (response.data && response.data.folderId) {
        return {
          id: response.data.folderId,
          name: response.data.name,
          color: response.data.color,
          parentId: response.data.parentFolderId,
          snippetCount: response.data.snippetCount || 0,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
          isRoot: response.data.folderId === response.data.createdBy,
          path: response.data.path || '/',
          description: response.data.description || '',
          tags: response.data.tags || [],
          children: []
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating folder:', error);
      
      // Handle specific error messages
      const errorMessage = error.response?.data?.message || error.message;
      const friendlyMessages = {
        "Cannot create root folders": "All folders must be created under your workspace",
        "Folder with this name already exists": "A folder with this name already exists here"
      };
      
      if (friendlyMessages[errorMessage]) {
        error.friendlyMessage = friendlyMessages[errorMessage];
      }
      
      throw error;
    }
  },

  // Update folder (with root folder restrictions)
  updateFolder: async (folderId, updateData) => {
    try {
      const userId = localStorage.getItem('userId');
      
      // Check if trying to update root folder with restricted operations
      if (folderId === userId) {
        // Root folder can only be renamed and description updated
        const allowedFields = ['name', 'description', 'color'];
        const restrictedFields = ['parentFolderId', 'parentId'];
        
        const hasRestrictedFields = restrictedFields.some(field => 
          updateData.hasOwnProperty(field)
        );
        
        if (hasRestrictedFields) {
          throw new Error("Cannot move your main workspace folder");
        }
        
        // Filter to only allowed fields
        const filteredData = {};
        allowedFields.forEach(field => {
          if (updateData.hasOwnProperty(field)) {
            filteredData[field] = updateData[field];
          }
        });
        updateData = filteredData;
      }

      const response = await api.put(`/api/folders/${folderId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating folder:', error);
      
      // Handle specific error messages
      const errorMessage = error.response?.data?.message || error.message;
      const friendlyMessages = {
        "Cannot move your main workspace folder": "Your main workspace cannot be moved",
        "Cannot delete your main workspace folder": "Your main workspace cannot be deleted"
      };
      
      if (friendlyMessages[errorMessage]) {
        error.friendlyMessage = friendlyMessages[errorMessage];
      }
      
      throw error;
    }
  },

  // Delete folder (with root folder protection)
  deleteFolder: async (folderId) => {
    try {
      const userId = localStorage.getItem('userId');
      
      // Prevent deletion of root folder
      if (folderId === userId) {
        throw new Error("Cannot delete your main workspace folder");
      }

      const response = await api.delete(`/api/folders/${folderId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting folder:', error);
      
      // Handle specific error messages
      const errorMessage = error.response?.data?.message || error.message;
      const friendlyMessages = {
        "Cannot delete your main workspace folder": "Your main workspace cannot be deleted"
      };
      
      if (friendlyMessages[errorMessage]) {
        error.friendlyMessage = friendlyMessages[errorMessage];
      }
      
      throw error;
    }
  },

  // Move folder to different parent (with root folder protection)
  moveFolder: async (folderId, newParentId) => {
    try {
      const userId = localStorage.getItem('userId');
      
      // Prevent moving root folder
      if (folderId === userId) {
        throw new Error("Cannot move your main workspace folder");
      }

      const response = await api.put(`/api/folders/${folderId}/move`, {
        parentId: newParentId
      });
      return response.data;
    } catch (error) {
      console.error('Error moving folder:', error);
      
      // Handle specific error messages
      const errorMessage = error.response?.data?.message || error.message;
      const friendlyMessages = {
        "Cannot move your main workspace folder": "Your main workspace cannot be moved"
      };
      
      if (friendlyMessages[errorMessage]) {
        error.friendlyMessage = friendlyMessages[errorMessage];
      }
      
      throw error;
    }
  },

  // Get snippets in a folder
  getFolderSnippets: async (folderId, page = 0, size = 20) => {
    try {
      const response = await api.get(`/api/snips/folder/${folderId}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching folder snippets:', error);
      throw error;
    }
  },

  // Utility functions
  isRootFolder: (folder, userId) => {
    return folder.id === userId || folder.folderId === userId;
  },

  // Get folder actions based on folder type
  getFolderActions: (folder, userId) => {
    const isRoot = folderService.isRootFolder(folder, userId);
    
    if (isRoot) {
      return [
        { label: "Rename", action: "rename", enabled: true },
        { label: "Add Folder", action: "addChild", enabled: true },
        { label: "Edit Description", action: "editDesc", enabled: true },
        { label: "Delete", action: "delete", enabled: false },
        { label: "Move", action: "move", enabled: false }
      ];
    } else {
      const hasChildren = folder.children && folder.children.length > 0;
      return [
        { label: "Rename", action: "rename", enabled: true },
        { label: "Add Folder", action: "addChild", enabled: true },
        { label: "Edit Description", action: "editDesc", enabled: true },
        { label: "Delete", action: "delete", enabled: !hasChildren },
        { label: "Move", action: "move", enabled: true }
      ];
    }
  }
};